import { Modal, View, SearchInput } from '@/components'
import { PropsOf } from '@codeleap/types'
import {
  Grid,
  SearchBar,
} from '@giphy/react-components'
import { createStyles } from '@codeleap/styles'
import { onUpdate, useState } from '@codeleap/hooks'
import { Gyphi } from '@/services'
import { theme } from '@/config'
import { useComponentSize, useIsMobile } from '@/hooks'
import { useChannelStateContext, useMessageComposer } from 'stream-chat-react'
import { Chat } from '@/config'
import { IGif } from '@giphy/js-types'
import { useRef } from 'react'

export type GIFPickerModalProps = PropsOf<typeof Modal> & {}

const MAX_WIDTH = 589
const defaultSearchKey = 'work'

export const GIFPickerModal = (props: GIFPickerModalProps) => {
  const { fetchGifs, term, activeChannel, setSearch } = Gyphi.useGIFs()
  const { channel } = useChannelStateContext()
  const messageComposer = useMessageComposer()
  const [loaded, setLoaded] = useState(false)
  const { ref: wrapperRef, size } = useComponentSize()
  const isMobile = useIsMobile()
  const isSending = useRef(false)

  async function handleGIFPress(gif: IGif, e) {
    e.preventDefault() // By default Gyphi component navigate to gif url

    try {
      if (isSending.current) return

      const aspectRatio = gif?.images?.fixed_width?.width / gif?.images?.fixed_width?.height
      isSending.current = true
      const quotedMessageId = messageComposer?.state?.getLatestValue?.()?.quotedMessage?.id
      props?.toggle?.()
      await Chat.Services.sendGiphy(
        {
          aspectRatio: aspectRatio || 1,
          channel,
          id: gif?.id?.toString(),
          title: gif.alt_text || 'GIF',
          quotedMessageId,
        },
      )
      if (quotedMessageId) {
        try { messageComposer?.setQuotedMessage?.(null) } catch (err) { console.warn('GIFPicker: composer setQuotedMessage(null) failed', err) }
        try { Chat.Store.clearQuotedMessage() } catch (err) { console.warn('GIFPicker: store clearQuotedMessage failed', err) }
      }
    } finally {
      isSending.current = false
    }
  }

  onUpdate(() => {
    if (props.visible) {
      setSearch(defaultSearchKey)
      setLoaded(true)
    } else {
      setLoaded(false)
      setSearch('')
    }
  }, [props.visible])

  function handleChange(text) {
    if (!text) {
      setSearch(defaultSearchKey)
      return
    }
    setSearch(text)
  }

  return (
    <Modal title='GIFs' style={['center', 'box:noPadding', { titleWrapper: styles.titleWrapper }]} {...props}>
      <View style={styles.wrapper} ref={wrapperRef}>
        <View style={['paddingHorizontal:2']}>
          <SearchInput
            key={`gif-search-${props.visible}`}
            placeholder='Search'
            debugName={'GIFPicker:Search'}
            onSearchChange={handleChange}
            debounce={300}
            style={['height:largeish', 'noError', 'mb:1.5']}
          />
        </View>

        <View style={{ opacity: 0, height: 0 }}>
          <SearchBar />
        </View>

        {loaded && (
          <View style={styles.scrollWrapper}>
            <Grid
              key={`${term} ${activeChannel?.user.username}`}
              columns={isMobile ? 2 : 3}
              width={size?.width - theme.spacing.value(4)}
              fetchGifs={fetchGifs}
              onGifClick={handleGIFPress}
            />
          </View>
        )}
      </View>
    </Modal>
  )
}

const styles = createStyles(theme => ({
  titleWrapper: {
    ...theme.spacing.padding(2),
  },
  wrapper: {
    ...theme.presets.column,
    maxWidth: MAX_WIDTH,
    height: '80svh',
  },
  scrollWrapper: {
    overflowY: 'auto',
    overflowX: 'hidden',
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.paddingBottom(2),
  },
}))

